import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Typography,
  Grid,
  Container,
  CircularProgress,
  Alert,
  Chip,
  Box,
  CardActions,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  useDeleteCampaignMutation,
  useGetCampaignByIdQuery,
} from "../../Apis/campaignApi";
import { withAdminAuth } from "../../HOC";
import { ActionButtons } from "../../Compnents/Ui";

export default withAdminAuth(function CampaignDetails() {
  const { campaignId } = useParams();
  const { data, isLoading, isError } = useGetCampaignByIdQuery(campaignId);
  const [deleteCampaign] = useDeleteCampaignMutation();
  const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error" | "info";
    }>({ open: false, message: "", severity: "info" });
  
    const handleCloseSnackbar = () => {
      setSnackbar({ ...snackbar, open: false });
    };

  if (isLoading) {
    return (
      <Container sx={{ my: 5, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ my: 5 }}>
        <Alert severity="error" sx={{ textAlign: "center" }}>
          Failed to load campaign details.
        </Alert>
      </Container>
    );
  }

  if (!data?.result) {
    return (
      <Container sx={{ my: 5 }}>
        <Alert severity="info" sx={{ textAlign: "center" }}>
          Campaign not found.
        </Alert>
      </Container>
    );
  }

  const campaign = data.result;

const confirmDelete = async () => {
    if (!selectedId) return;

    try {
      await deleteCampaign({ id: selectedId }).unwrap();

      setSnackbar({
        open: true,
        message: "Campaign Deleted Successfully",
        severity: "success",
      });

      navigate(-1);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.data?.errorMessages?.[0] || "Delete failed",
        severity: "error",
      });
    } finally {
      setOpenDialog(false);
      setSelectedId(null);
    }
  };

  return (
    <Container sx={{ my: 5 }}>
      <Card sx={{ p: 2 }}>
        <CardHeader
          title={
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ color: "#512384", textAlign: "center" }}
            >
              {campaign.name}
            </Typography>
          }
        />

        <CardContent>
          <Grid container spacing={3}>
            {/* Image */}
            <Grid size={{ xs: 12, md: 4 }} textAlign="center">
              <CardMedia
                component="img"
                image={campaign.imageUrl}
                alt={campaign.name}
                sx={{
                  borderRadius: 2,
                  maxHeight: 300,
                  objectFit: "cover",
                  boxShadow: 3,
                }}
              />
            </Grid>

            {/* Details */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Box mb={3}>
                <Typography variant="h5" fontWeight="600">
                  Description
                </Typography>
                <Box
                  sx={{ mt: 1 }}
                  dangerouslySetInnerHTML={{ __html: campaign.description }}
                ></Box>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography>
                    <strong>Start Date:</strong>{" "}
                    {new Date(campaign.startDate).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography>
                    <strong>End Date:</strong>{" "}
                    {new Date(campaign.endDate).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography>
                    <strong>Status:</strong>{" "}
                    {campaign.isActive ? (
                      <Chip label="Active" color="success" />
                    ) : (
                      <Chip label="Inactive" color="error" />
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>

        <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
          <ActionButtons
            onEdit={() => navigate(`/Campaign/Edit/${campaign.id}`)}
            onDelete={() => {
                          setSelectedId(campaign.id);
                          setOpenDialog(true);
                        }}
            onBack={() => navigate(-1)}
          />
        </CardActions>
      </Card>
      <Snackbar
              open={snackbar.open}
              autoHideDuration={5000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to delete this campaign?
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)} color="inherit">
                  Cancel
                </Button>
      
                <Button onClick={confirmDelete} color="error" variant="contained">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
    </Container>
  );
});
